/**
 * Sidekick push-consent soft prompt.
 *
 * Two hard rules this module enforces:
 *
 *   1. The native Notification.requestPermission() prompt is NEVER fired
 *      automatically on page load. It only fires when the user has
 *      explicitly clicked the "Allow notifications" button in our own
 *      soft prompt UI. (See requestPushPermission below.)
 *
 *   2. We only show the soft prompt when push is actually usable in this
 *      browser AND the user hasn't already responded to a native prompt
 *      for our origin. If Notification is missing, or permission is
 *      already 'granted' or 'denied', the prompt is hidden silently —
 *      no flash, no nag.
 *
 * Audit storage:
 *   When the user makes a choice, we POST to Klaviyo's client-side
 *   profile-upsert endpoint with the consent state plus the standard
 *   audit metadata (timestamp, source form, page URL, country, exact
 *   wording shown). This stores push consent independently of the
 *   email/SMS subscriptions captured upstream by the host form.
 *
 * Exposed surface on window.SidekickPushConsent:
 *   init(rootEl, opts)        wire up a push-consent block. Auto-hides
 *                             when unsupported or already decided.
 *   isSupported()             true if Notification API is usable here
 *   getCurrentPermission()    'unsupported' | 'default' | 'granted' | 'denied'
 *
 * Expected init opts:
 *   email                 (string)  upstream profile email, if captured
 *   phone                 (string)  upstream profile phone in E.164, if captured
 *   country               (string)  selected country, optional
 *   klaviyoCompanyId      (string)  Klaviyo company id ('public key')
 *   klaviyoRevision       (string)  Klaviyo API revision header
 *   source                (string)  source form identifier (e.g. 'BCC inline')
 */
(function () {
  'use strict';

  /* Push is "supported" in our terms if the browser exposes the
     Notification API. We deliberately do NOT require a service worker
     here — actual push delivery wiring is downstream, but capturing the
     user's intent + their browser-level permission is valuable on its
     own. */
  function isSupported() {
    return typeof window !== 'undefined' &&
           'Notification' in window &&
           typeof Notification.requestPermission === 'function';
  }

  function getCurrentPermission() {
    if (!isSupported()) return 'unsupported';
    try {
      return Notification.permission || 'default';
    } catch (e) {
      return 'unsupported';
    }
  }

  function init(rootEl, opts) {
    if (!rootEl) return;
    if (rootEl.dataset.pushInit === '1') return;
    rootEl.dataset.pushInit = '1';

    opts = opts || {};

    /* If push is unavailable, or the user has already given a final
       answer at the browser level, we don't show the prompt at all. The
       container stays hidden — there's no point asking again from inside
       a modal when the browser will silently ignore the request. */
    var perm = getCurrentPermission();
    if (perm === 'unsupported' || perm === 'granted' || perm === 'denied') {
      rootEl.hidden = true;
      if (typeof opts.onComplete === 'function') opts.onComplete();
      return;
    }

    rootEl.hidden = false;

    var promptEl = rootEl.querySelector('[data-push-prompt]');
    var thanksEl = rootEl.querySelector('[data-push-thanks]');
    var allowBtn = rootEl.querySelector('[data-push-allow]');
    var dismissBtn = rootEl.querySelector('[data-push-dismiss]');

    if (allowBtn) {
      allowBtn.addEventListener('click', function () {
        requestPushPermission(rootEl, opts, promptEl, thanksEl, allowBtn);
      });
    }
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        /* Soft decline. We do NOT call Notification.requestPermission()
           here — the user chose the No path, so we respect that without
           involving the browser-level state machine. We DO record the
           decline against the Klaviyo profile so we have an audit trail. */
        recordPushConsent(opts, 'NOT_SUBSCRIBED', 'default', 'soft_dismiss');
        rootEl.hidden = true;
        if (typeof opts.onComplete === 'function') opts.onComplete();
      });
    }
  }

  function notifyComplete(opts, delayMs) {
    if (typeof opts.onComplete !== 'function') return;
    if (delayMs) {
      setTimeout(opts.onComplete, delayMs);
    } else {
      opts.onComplete();
    }
  }

  function requestPushPermission(rootEl, opts, promptEl, thanksEl, allowBtn) {
    if (!isSupported()) {
      rootEl.hidden = true;
      return;
    }

    if (allowBtn) {
      allowBtn.setAttribute('disabled', 'true');
      allowBtn.textContent = 'Asking your browser…';
    }

    /* Notification.requestPermission has two historical signatures —
       a callback-based one and a Promise-based one. We feature-detect
       so older browsers (older Safari, etc.) still work. */
    var maybePromise;
    try {
      maybePromise = Notification.requestPermission(function (resultLegacy) {
        finishRequest(rootEl, opts, promptEl, thanksEl, resultLegacy);
      });
    } catch (e) {
      maybePromise = null;
    }

    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.then(function (result) {
        finishRequest(rootEl, opts, promptEl, thanksEl, result);
      }, function () {
        finishRequest(rootEl, opts, promptEl, thanksEl, 'denied');
      });
    }
    /* If callback-style was used, finishRequest is already invoked by
       the callback above and we do nothing further here. */
  }

  function finishRequest(rootEl, opts, promptEl, thanksEl, result) {
    var normalized = result === 'granted' || result === 'denied' || result === 'default'
      ? result
      : getCurrentPermission();

    if (normalized === 'granted') {
      recordPushConsent(opts, 'SUBSCRIBED', 'granted', 'soft_allow');
      if (promptEl) promptEl.hidden = true;
      if (thanksEl) thanksEl.hidden = false;
      notifyComplete(opts, thanksEl ? 1200 : 0);
    } else {
      /* Denied or dismissed-without-decision. We treat both the same way
         here: no subscription, record the state, hide the prompt. */
      recordPushConsent(opts, 'NOT_SUBSCRIBED', normalized, 'native_decline');
      rootEl.hidden = true;
      notifyComplete(opts, 0);
    }
  }

  /* Send a per-channel consent record to Klaviyo, independent of the
     email/SMS list subscription created by the upstream form. We use the
     /client/profiles/ endpoint which upserts a profile by identifier
     without re-subscribing to any list, so this can be called safely
     after the parent form has already subscribed the user. */
  function recordPushConsent(opts, consentState, permissionState, trigger) {
    if (!opts || !opts.klaviyoCompanyId) return;
    if (!opts.email && !opts.phone) return;

    var endpoint = 'https://a.klaviyo.com/client/profiles/?company_id=' +
                   encodeURIComponent(opts.klaviyoCompanyId);

    var consentText = opts.pushConsentText || PUSH_CONSENT_TEXT;
    var consentVersion = opts.pushConsentVersion || PUSH_CONSENT_VERSION;

    var properties = {
      'Push Marketing Consent': consentState,
      'Push Permission State': permissionState || 'unknown',
      'Last Push Consent Trigger': trigger || '',
      'Last Push Consent Text': consentText,
      'Last Push Consent Version': consentVersion,
      'Last Push Consent Timestamp': new Date().toISOString(),
      'Last Push Consent Source': opts.source || '',
      'Last Push Consent Page URL': window.location ? window.location.href : ''
    };
    if (opts.campaignName) properties['Campaign Name'] = opts.campaignName;
    if (opts.country) properties.Country = opts.country;

    var attributes = { properties: properties };
    if (opts.email) attributes.email = opts.email;
    if (opts.phone) attributes.phone_number = opts.phone;

    var payload = {
      data: {
        type: 'profile',
        attributes: attributes
      }
    };

    try {
      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'revision': opts.klaviyoRevision || '2026-04-15'
        },
        body: JSON.stringify(payload),
        /* keepalive so the request survives a modal close / page nav */
        keepalive: true
      }).catch(function () { /* network errors are non-fatal for consent recording */ });
    } catch (e) {
      /* ignore */
    }
  }

  /* The exact wording the user saw, kept in sync with
     snippets/push-consent-prompt.liquid. Any wording change must bump
     PUSH_CONSENT_VERSION below so we can tell new consents apart from
     historical ones in Klaviyo. */
  var PUSH_CONSENT_TEXT = 'Want Sidekick Soda notifications on this device? Get occasional alerts about product drops, offers, back-in-stock updates and campaign news. You can turn these off any time in your browser or device settings.';
  var PUSH_CONSENT_VERSION = '2026-05-20-v1';

  window.SidekickPushConsent = {
    init: init,
    isSupported: isSupported,
    getCurrentPermission: getCurrentPermission
  };
})();
